import { BaseDocument } from "./base";
import { EUserType } from "./enums";
import { FreelancerExperienceLevel, FreelancerRole } from "./freelancer";
import { UserId } from "./helpers";

export enum ETaskStatus {
    GENERATED = -1, // generated by system
    DRAFT = 0, // draft task - created by pm but not available for platform
    REQUESTED_APPROVAL = 1, // requested approval by employer
    REJECTED_APPROVAL_REQUEST = 2, // rejected approval request by employer
    WAIT_FOR_PAYMENT = 3, // approved by employer or pm and ready to be assigned to freelancers
    WAIT_FOR_FREELANCER = 4, // wait for applications
    REASSIGN = 5, // reassign task - not yet accepted by freelancers
    IN_PROGRESS = 6, // in progress
    DELIVERY_PROVIDED = 7, // delivery provided by freelancer
    IN_REVISION = 8, // rework required
    IN_REVISION_DELIVERY_PROVIDED = 9, // rework required and revision delivery provided
    DELIVERY_ACCEPTED = 10, // delivery accepted
    REQUESTED_COMPLETION = 11, // requested completion by pm
    REJECTED_COMPLETION_REQUEST = 12, // rejected completion by employer
    COMPLETED = 13, // completed
    CANCELLED = 14, // pm cancelled task / task 
}

export enum ETaskPriority {
    NONE,
    LOW,
    MEDIUM,
    HIGH,
}

export interface TaskComment {
    id: string,
    message: string,
    userId: UserId,
    createdAt: Date,
    likedBy: UserId[],
}

export interface TaskRevision {
    id: string,
    revisionNumber: number,
    revisionDate: Date,
    revisionBy: UserId,
    revisionMessage: string,
}

export interface Task extends BaseDocument {

    createdBy: UserId,

    requestedApprovalAt: Date | null,
    rejectedApprovalRequestAt: Date | null,
    waitForPaymentAt: Date | null,
    requestedCompletionAt: Date | null,
    rejectedCompletionRequestAt: Date | null,
    waitForFreelancerAt: Date | null,
    reassignAt: Date | null,
    inProgressAt: Date | null,
    deliveryProvidedAt: Date | null,
    inRevisionAt: Date | null,
    deliveryAcceptedAt: Date | null,
    completedAt: Date | null,
    cancelledAt: Date | null,
    payable: boolean,
    creditsOccupiedAt: Date | null,
    creditsConsumedAt: Date | null,

    numberRevisions: number,

    rejectApprovalRequestReasons: string[],
    rejectCompletionRequestReasons: string[],

    approvedBy: UserId | null,
    completedBy: UserId | null,

    markAsCompleteFreelancer: boolean,
    markAsCompletePm: boolean,
    markAsCompleteEmployer: boolean,
    markAsCompleteAdmin: boolean,
    currentStatus: ETaskStatus,

    estimatedCredits: number,
    estimatedHours: number,
    actualCredits: number,
    actualHours: number,

    projectId: string,
    projectRank: number, // rank of task in project
    dependsOnTasksCompleted: string[], // task ids of tasks that this task depends on
    currentlyAssignedTo: string | null, // user id
    assignedFreelancerHistory: {
        unassignedAt: Date,
        freelancerId: string,
        revisions: TaskRevision[],
    }[],

    requiredExperience: FreelancerExperienceLevel,
    requiredRole: FreelancerRole,
    requiredSkills: string[],
    requiredTools: string[],

    priority: ETaskPriority,

    freelancerShortlistByPm: string[],
    freelancerShortlistByEmployer: string[],

    type: string,
    title: string,
    description: string,
    briefDescription: string,
    definitionOfDone: string,
    progressPercentage: number,
    previewLink: string | null,

    startDate: Date | null,
    dueDate: Date | null,

    revisions: TaskRevision[],
    comments: TaskComment[],
}

export const createDefaultTask = (): Task => ({

    createdBy: { id: '', userType: EUserType.FREELANCER },

    requestedApprovalAt: null,
    rejectedApprovalRequestAt: null,
    requestedCompletionAt: null,
    rejectedCompletionRequestAt: null,
    waitForFreelancerAt: null,
    reassignAt: null,
    inProgressAt: null,
    deliveryProvidedAt: null,
    inRevisionAt: null,
    deliveryAcceptedAt: null,
    waitForPaymentAt: null,
    completedAt: null,
    cancelledAt: null,
    payable: false,
    creditsOccupiedAt: null,
    creditsConsumedAt: null,
    numberRevisions: 0,


    rejectApprovalRequestReasons: [],
    rejectCompletionRequestReasons: [],

    approvedBy: null,
    completedBy: null,

    markAsCompleteFreelancer: false,
    markAsCompletePm: false,
    markAsCompleteEmployer: false,
    markAsCompleteAdmin: false,
    currentStatus: ETaskStatus.DRAFT,

    estimatedCredits: 0,
    estimatedHours: 0,
    actualCredits: 0,
    actualHours: 0,

    projectId: '',
    projectRank: 0, // rank of task in project
    dependsOnTasksCompleted: [], // task ids of tasks that this task depends on
    currentlyAssignedTo: null, // user id
    assignedFreelancerHistory: [],

    requiredExperience: FreelancerExperienceLevel.T1,
    requiredRole: FreelancerRole.OTHER,
    requiredSkills: [],
    requiredTools: [],

    priority: ETaskPriority.MEDIUM,

    freelancerShortlistByPm: [],
    freelancerShortlistByEmployer: [],

    type: '',
    title: '',
    briefDescription: '',
    description: '',
    definitionOfDone: '',
    progressPercentage: 0,
    previewLink: null,

    startDate: null,
    dueDate: null,

    revisions: [],
    comments: [],
})



export interface ITask extends Partial<Task> { };